import React, { useEffect, useState } from "react";
import env from "../../config/env.js";
import {
  Box,
  Typography,
  Paper,
  Button,
  useTheme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Snackbar,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Card,
  CardContent,
  Grid,
  Divider,
  TablePagination
} from "@mui/material";
import {
  Print,
  ExpandMore,
  Assessment,
  TrendingUp,
  Person,
  Category,
  Business
} from "@mui/icons-material";
import AcessoRestrito from '../../components/AcessoRestrito';

export default function RelatorioServicos() {
  const theme = useTheme();
  const [dados, setDados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState("servico");
  const [order, setOrder] = useState("asc");

  useEffect(() => {
    carregarRelatorio();
  }, []);

  const carregarRelatorio = async () => {
    // Log de auditoria removido - agora é feito no backend
  };

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const calcularTotalAtendimentos = (servico) => {
    return servico.categorias.reduce((totalCategoria, categoria) => {
      return totalCategoria + categoria.colaboradores.reduce((totalColaborador, colaborador) => {
        return totalColaborador + colaborador.totalAtendimentos;
      }, 0);
    }, 0);
  };

  const calcularTotalGeral = () => {
    return dados.reduce((total, servico) => {
      return total + calcularTotalAtendimentos(servico);
    }, 0);
  };

  const encontrarTopColaborador = () => {
    let topColaborador = { nome: "", totalAtendimentos: 0 };
    
    dados.forEach(servico => {
      servico.categorias.forEach(categoria => {
        categoria.colaboradores.forEach(colaborador => {
          if (colaborador.totalAtendimentos > topColaborador.totalAtendimentos) {
            topColaborador = colaborador;
          }
        });
      });
    });
    
    return topColaborador;
  };

  const dadosOrdenados = React.useMemo(() => {
    return dados.sort((a, b) => {
      const isAsc = order === "asc";
      if (orderBy === "servico") {
        const servicoA = a.servico || "";
        const servicoB = b.servico || "";
        return isAsc ? servicoA.localeCompare(servicoB) : servicoB.localeCompare(servicoA);
      } else if (orderBy === "total") {
        const totalA = calcularTotalAtendimentos(a);
        const totalB = calcularTotalAtendimentos(b);
        return isAsc ? totalA - totalB : totalB - totalA;
      }
      return 0;
    });
  }, [dados, order, orderBy]);

  const dadosPaginados = dadosOrdenados.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const gerarPDF = () => {
    const novaJanela = window.open("", "_blank", "width=1000,height=800,scrollbars=yes");
    const isDarkMode = theme.palette.mode === 'dark';

    if (!novaJanela) {
      alert("Não foi possível abrir a nova janela.");
      return;
    }

    const style = `
      <style>
        body {
          font-family: Arial, sans-serif;
          margin: 30px;
          background-color: ${isDarkMode ? '#121212' : '#ffffff'};
          color: ${isDarkMode ? '#ffffff' : '#000000'};
        }
        h1 {
          text-align: center;
          color: ${isDarkMode ? '#90caf9' : '#1976d2'};
          margin-bottom: 30px;
        }
        .resumo {
          display: flex;
          justify-content: space-around;
          margin-bottom: 30px;
          flex-wrap: wrap;
        }
        .card-resumo {
          background-color: ${isDarkMode ? '#1e1e1e' : '#f5f5f5'};
          padding: 20px;
          border-radius: 8px;
          text-align: center;
          min-width: 200px;
          margin: 10px;
        }
        .card-resumo h3 {
          color: ${isDarkMode ? '#90caf9' : '#1976d2'};
          margin: 0 0 10px 0;
        }
        .servico {
          margin-top: 30px;
          border: 1px solid ${isDarkMode ? '#424242' : '#e0e0e0'};
          border-radius: 8px;
          overflow: hidden;
        }
        .servico-header {
          background-color: ${isDarkMode ? '#1e1e1e' : '#f5f5f5'};
          padding: 15px;
          font-weight: bold;
          color: ${isDarkMode ? '#90caf9' : '#1976d2'};
          font-size: 18px;
        }
        .categoria {
          margin: 15px;
          border-left: 3px solid ${isDarkMode ? '#90caf9' : '#1976d2'};
          padding-left: 15px;
        }
        .categoria-header {
          font-weight: bold;
          color: ${isDarkMode ? '#90caf9' : '#1976d2'};
          margin-bottom: 10px;
        }
        table {
          width: 100%;
          border-collapse: collapse;
          margin-top: 10px;
        }
        th, td {
          padding: 8px;
          text-align: left;
          border-bottom: 1px solid ${isDarkMode ? '#424242' : '#e0e0e0'};
        }
        th {
          background-color: ${isDarkMode ? '#2a2a2a' : '#e8e8e8'};
          color: ${isDarkMode ? '#90caf9' : '#1976d2'};
        }
        .total-servico {
          text-align: right;
          font-weight: bold;
          padding: 10px;
          background-color: ${isDarkMode ? '#1e1e1e' : '#f5f5f5'};
          color: ${isDarkMode ? '#90caf9' : '#1976d2'};
        }
        .total-geral {
          font-size: 24px;
          font-weight: bold;
          margin-top: 40px;
          text-align: center;
          color: ${isDarkMode ? '#90caf9' : '#1976d2'};
          padding: 20px;
          background-color: ${isDarkMode ? '#1e1e1e' : '#f5f5f5'};
          border-radius: 8px;
        }
        .botoes {
          margin-top: 30px;
          text-align: center;
        }
        button {
          background-color: ${isDarkMode ? '#1976d2' : '#1976d2'};
          color: white;
          padding: 10px 20px;
          margin: 5px;
          border: none;
          border-radius: 5px;
          font-size: 16px;
          cursor: pointer;
          width: 200px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }
        button:hover {
          background-color: ${isDarkMode ? '#1565c0' : '#1565c0'};
        }
      </style>
    `;

    const topColaborador = encontrarTopColaborador();
    const totalGeral = calcularTotalGeral();

    let conteudo = `
      <html>
      <head>
        <title>Relatório de Serviços</title>
        ${style}
      </head>
      <body>
        <h1>Relatório de Atendimentos por Serviços</h1>
        
        <div class="resumo">
          <div class="card-resumo">
            <h3>Total de Atendimentos</h3>
            <div style="font-size: 24px; font-weight: bold;">${totalGeral}</div>
          </div>
          <div class="card-resumo">
            <h3>Serviços Ativos</h3>
            <div style="font-size: 24px; font-weight: bold;">${dados.length}</div>
          </div>
          <div class="card-resumo">
            <h3>Top Colaborador</h3>
            <div style="font-size: 18px; font-weight: bold;">${topColaborador.nome}</div>
            <div style="font-size: 16px;">${topColaborador.totalAtendimentos} atendimentos</div>
          </div>
        </div>
    `;

    dados.forEach((servico) => {
      const totalServico = calcularTotalAtendimentos(servico);
      
      conteudo += `
        <div class="servico">
          <div class="servico-header">
            ${servico.servico}
          </div>
      `;

      servico.categorias.forEach((categoria) => {
        const totalCategoria = categoria.colaboradores.reduce((total, colab) => 
          total + colab.totalAtendimentos, 0
        );

        conteudo += `
          <div class="categoria">
            <div class="categoria-header">${categoria.categoria}</div>
            <table>
              <thead>
                <tr>
                  <th>Colaborador</th>
                  <th>Total de Atendimentos</th>
                </tr>
              </thead>
              <tbody>
        `;

        categoria.colaboradores.forEach((colaborador) => {
          conteudo += `
            <tr>
              <td>${colaborador.nome}</td>
              <td>${colaborador.totalAtendimentos}</td>
            </tr>
          `;
        });

        conteudo += `
              </tbody>
            </table>
            <div style="text-align: right; font-weight: bold; margin-top: 10px;">
              Total da Categoria: ${totalCategoria}
            </div>
          </div>
        `;
      });

      conteudo += `
          <div class="total-servico">
            Total do Serviço: ${totalServico}
          </div>
        </div>
      `;
    });

    conteudo += `
        <div class="total-geral">
          Total Geral de Atendimentos: ${totalGeral}
        </div>
        
        <div class="botoes">
          <button onclick="window.print()">
            <span style="font-size: 18px;">🖨️</span>
            Imprimir
          </button>
        </div>
      </body>
      </html>
    `;

    novaJanela.document.write(conteudo);
    novaJanela.document.close();
  };

  const topColaborador = encontrarTopColaborador();
  const totalGeral = calcularTotalGeral();

  return (
    <AcessoRestrito modulo="relatorioservicos">
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 0,
          height: '80vh',
          width: '100%',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {error && (
          <Snackbar 
            open={!!error} 
            autoHideDuration={env.NOTIFICATION_DURATION || 6000} 
            onClose={() => setError(null)}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          >
            <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
              {error}
            </Alert>
          </Snackbar>
        )}

        {/* Cabeçalho */}
        <Box sx={{ p: 3, pb: 2 }} display="flex" justifyContent="space-between" flexWrap="wrap">
          <Typography variant="h5" mb={1}>Relatório de Serviços</Typography>
          <Button
            variant="contained"
            startIcon={<Print />}
            onClick={gerarPDF}
            disabled={loading}
          >
            Imprimir Relatório
          </Button>
        </Box>

        {/* Cards de Resumo */}
        <Box sx={{ px: 3, pb: 2 }}>
          <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              background: theme.palette.mode === 'dark' 
                ? 'linear-gradient(135deg, #1e1e1e 0%, #2a2a2a 100%)' 
                : 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
              border: `1px solid ${theme.palette.mode === 'dark' ? '#424242' : '#e0e0e0'}`
            }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <Assessment sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                <Typography variant="h4" fontWeight="bold" color="primary">
                  {totalGeral}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total de Atendimentos
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              background: theme.palette.mode === 'dark' 
                ? 'linear-gradient(135deg, #1e1e1e 0%, #2a2a2a 100%)' 
                : 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
              border: `1px solid ${theme.palette.mode === 'dark' ? '#424242' : '#e0e0e0'}`
            }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <Business sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                <Typography variant="h4" fontWeight="bold" color="primary">
                  {dados.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Serviços Ativos
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              background: theme.palette.mode === 'dark' 
                ? 'linear-gradient(135deg, #1e1e1e 0%, #2a2a2a 100%)' 
                : 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
              border: `1px solid ${theme.palette.mode === 'dark' ? '#424242' : '#e0e0e0'}`
            }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <Person sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                <Typography variant="h6" fontWeight="bold" color="primary" noWrap>
                  {topColaborador.nome}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Top Colaborador
                </Typography>
                <Chip 
                  label={`${topColaborador.totalAtendimentos} atendimentos`}
                  color="primary"
                  size="small"
                  sx={{ mt: 1 }}
                />
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              background: theme.palette.mode === 'dark' 
                ? 'linear-gradient(135deg, #1e1e1e 0%, #2a2a2a 100%)' 
                : 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
              border: `1px solid ${theme.palette.mode === 'dark' ? '#424242' : '#e0e0e0'}`
            }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <TrendingUp sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                <Typography variant="h4" fontWeight="bold" color="primary">
                  {dados.reduce((total, servico) => 
                    total + servico.categorias.length, 0
                  )}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Categorias
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        </Box>

        {/* Container da Tabela */}
        <TableContainer 
          component={Paper} 
          sx={{ 
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            overflow: 'auto',
            height: '100%',
            maxHeight: '100%',
            mx: 3,
            width: 'calc(100% - 48px)',
            mb: 9
          }}
        >
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <CircularProgress />
            </Box>
          ) : dadosPaginados.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <Typography variant="h6" color="text.secondary">
                Nenhum serviço encontrado.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ p: 2 }}>
              {dadosPaginados.map((servico, index) => {
            const totalServico = calcularTotalAtendimentos(servico);
            
            return (
              <Accordion
                key={index}
                expanded={expanded === `panel${index}`}
                onChange={handleAccordionChange(`panel${index}`)}
                sx={{
                  background: 'transparent',
                  '&:before': { display: 'none' },
                  borderBottom: `1px solid ${theme.palette.mode === 'dark' ? '#424242' : '#e0e0e0'}`
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMore />}
                  sx={{
                    background: theme.palette.mode === 'dark' ? '#1e1e1e' : '#f5f5f5',
                    '&:hover': {
                      background: theme.palette.mode === 'dark' ? '#2a2a2a' : '#e8e8e8'
                    }
                  }}
                >
                  <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                    <Box display="flex" alignItems="center" gap={2}>
                      <Business color="primary" />
                      <Typography variant="h6" fontWeight="bold">
                        {servico.servico}
                      </Typography>
                      <Chip 
                        label={`${servico.categorias.length} categorias`}
                        size="small"
                        color="secondary"
                      />
                    </Box>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Typography variant="body2" color="text.secondary">
                        Total: {totalServico} atendimentos
                      </Typography>
                    </Box>
                  </Box>
                </AccordionSummary>
                
                <AccordionDetails sx={{ p: 0 }}>
                  {servico.categorias.map((categoria, catIndex) => {
                    const totalCategoria = categoria.colaboradores.reduce((total, colab) => 
                      total + colab.totalAtendimentos, 0
                    );
                    
                    return (
                      <Box key={catIndex} sx={{ p: 2 }}>
                        <Box display="flex" alignItems="center" gap={1} mb={2}>
                          <Category color="primary" />
                          <Typography variant="h6" fontWeight="bold" color="primary">
                            {categoria.categoria}
                          </Typography>
                          <Chip 
                            label={`${categoria.colaboradores.length} colaboradores`}
                            size="small"
                            variant="outlined"
                          />
                        </Box>
                        
                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 'bold' }}>Colaborador</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }} align="right">
                                  Total de Atendimentos
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {categoria.colaboradores.map((colaborador, colabIndex) => (
                                <TableRow key={colabIndex} hover>
                                  <TableCell>
                                    <Box display="flex" alignItems="center" gap={1}>
                                      <Person fontSize="small" color="action" />
                                      {colaborador.nome}
                                    </Box>
                                  </TableCell>
                                  <TableCell align="right">
                                    <Chip 
                                      label={colaborador.totalAtendimentos}
                                      color="primary"
                                      size="small"
                                    />
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                        
                        <Box display="flex" justifyContent="flex-end" mt={2}>
                          <Typography variant="body2" fontWeight="bold" color="primary">
                            Total da Categoria: {totalCategoria}
                          </Typography>
                        </Box>
                        
                        {catIndex < servico.categorias.length - 1 && (
                          <Divider sx={{ my: 2 }} />
                        )}
                      </Box>
                    );
                  })}
                  
                  <Box sx={{ 
                    p: 2, 
                    background: theme.palette.mode === 'dark' ? '#1e1e1e' : '#f5f5f5',
                    borderTop: `1px solid ${theme.palette.mode === 'dark' ? '#424242' : '#e0e0e0'}`
                  }}>
                    <Typography variant="h6" fontWeight="bold" color="primary" textAlign="right">
                      Total do Serviço: {totalServico} atendimentos
                    </Typography>
                  </Box>
                </AccordionDetails>
              </Accordion>
            );
          })}
            </Box>
          )}
        </TableContainer>

        {/* Paginação Fixa */}
        <TablePagination
          component="div"
          count={dados.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25]}
          labelRowsPerPage="Itens por página"
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            backgroundColor: 'background.paper',
            borderTop: 1,
            borderColor: 'divider',
            width: '100%',
            px: 3,
            py: 1.5,
            boxSizing: 'border-box',
            zIndex: 1000,
            minHeight: '60px'
          }}
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
        />

        {/* Resumo Fixo */}
        <Box sx={{ 
          position: 'fixed',
          bottom: '60px',
          left: 0,
          right: 0,
          p: 2,
          backgroundColor: 'background.paper',
          borderTop: 1,
          borderColor: 'divider',
          zIndex: 999,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
          }}>
          <Typography variant="h6">
            Total Geral: {totalGeral} atendimentos
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {dados.length} serviço(s) encontrado(s)
            </Typography>
        </Box>
      </Box>
    </AcessoRestrito>
  );
} 